<template>
  <el-popover
    placement="top-end"
    :width="200"
    trigger="click"
    v-model:visible="visible"
    popper-class="language-selector-popper"
  >
    <div class="language-list">
      <div
        class="language-item"
        v-for="(item, index) in displayLanguages"
        :key="index"
        :class="{ active: item.value === modelValue }"
        @click="handleLanguageChange(item.value)"
      >
        {{ item.label }}
        <font-icon v-if="item.value === modelValue" icon="icon-gou" />
      </div>
    </div>
    <template #reference>
      <div class="language-selector">
        <font-icon icon="icon-yuyan" class="icon" />
        <span class="current-language">{{ currentLanguageLabel }}</span>
        <font-icon
          class="arrow-icon"
          icon="icon-xiala"
          :class="{ open: visible }"
        />
      </div>
    </template>
  </el-popover>
</template>

<script setup>
import { get } from "lodash";
import { storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { useYChannelStoreHook } from "@/distributions/consumer/y/store/channel";
import { channelI18nMaps } from "@/plugins/i18n";
import { useChannelStoreHook } from "@/store/modules/channel";
const props = defineProps({
  modelValue: {
    type: String,
    default: "zh"
  },
  useChannelLanguages: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(["update:modelValue", "change"]);

const { t } = useI18n();

const DEFAULT_LANGUAGES = computed(() => {
  return [
    {
      label: t("shared.common.languageType.zh"),
      value: "zh"
    },
    {
      label: t("shared.common.languageType.en"),
      value: "en"
    },
    {
      label: t("shared.common.languageType.zh_TW"),
      value: "zh_TW"
    },
    {
      label: t("shared.common.languageType.jp"),
      value: "jp"
    }
  ];
});

const channelStore = useChannelStoreHook();
const YChannelStore = useYChannelStoreHook();

const { channel } = storeToRefs(channelStore);

const visible = ref(false);

const displayLanguages = computed(() => {
  const channelLanguages = get(channel.value, "i18n.languages", []);
  if (YChannelStore.configs && !props.useChannelLanguages) {
    return DEFAULT_LANGUAGES.value;
  } else {
    const _languages = DEFAULT_LANGUAGES.value.filter(item => {
      return channelLanguages.includes(
        channelI18nMaps[item.value] || item.value
      );
    });
    return _languages.length > 0 ? _languages : DEFAULT_LANGUAGES.value;
  }
});

const currentLanguageLabel = computed(() => {
  return (
    displayLanguages.value.find(item => item.value === props.modelValue)
      ?.label ?? ""
  );
});

const handleLanguageChange = value => {
  emit("update:modelValue", value);
  emit("change", value);
  visible.value = false;
};

watch(
  displayLanguages,
  () => {
    if (!currentLanguageLabel.value) {
      handleLanguageChange(displayLanguages.value[0].value);
    }
  },
  { immediate: true }
);

defineOptions({
  name: "LanguageSelector"
});
</script>

<style lang="scss" scoped>
.language-selector {
  display: flex;
  align-items: center;
  color: var(--y-language-selector-color, #ffffff);
  cursor: pointer;
}

.icon {
  font-size: 16px;
}

.current-language {
  font-size: var(--y-language-selector-size, 16px);
  font-family: Roboto, Roboto;
  font-weight: 500;
  line-height: 20px;
  margin-left: 4px;
  white-space: nowrap;
}

.icon-xiala {
  width: 10px;
  font-size: 10px;
  margin-left: 8px;
  transition: transform 0.3s;
  &.open {
    transform: rotate(-180deg);
  }
}

.language-list {
  padding: 0 8px;

  .language-item {
    padding: 20px 0;
    font-size: 16px;
    font-family: Barlow, Barlow;
    font-weight: 500;
    color: #696969;
    line-height: 16px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;

    &:not(:last-child) {
      border-bottom: 1px solid #eeeeee;
    }

    &:first-child {
      padding-top: 8px;
    }

    &:last-child {
      padding-bottom: 8px;
    }

    &:hover,
    &.active {
      color: var(--y-text-color);
    }

    .icon-gou {
      font-size: 12px;
    }
  }
}
</style>
<style>
.el-popper.language-selector-popper {
  border-radius: 0;
}
</style>
